import React, { FC, useCallback, useMemo, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { MenuButton, SizesPopup } from 'ui';
import { filterStyleVariantSizes } from '../../garments';
import { HomeState } from '../store/homeState';
import * as S from './homeSkin.styles';
import { homeStateExt } from '../store/homeStateExt';
import { setGarment } from '../actions/homeGarment';

interface Item {
  id: string;
  size: string;
}

/**
 * HomeSizes custom hook.
 */
const useSizes = () => {
  const { dispatch, getState } = useStore<HomeState>();

  const ctxGarmentId = useSelector((state: HomeState) => state.garmentId);
  const ctxStyle = useSelector((state: HomeState) => state.style);

  const sizes = useMemo(() => {
    if (!ctxStyle) {
      return [];
    }

    const style = homeStateExt.style!;
    const sizes: Item[] = filterStyleVariantSizes(style, ctxStyle.variant).map(g => ({
      id: g.id,
      size: g.size,
    }));

    return sizes;
  }, [ctxStyle]);

  const size = useMemo(() => {
    if (!ctxGarmentId) {
      return undefined;
    }

    const garment = homeStateExt.garment!;
    return garment.size;
  }, [ctxGarmentId]);

  // Selection state
  const [isSelected, setSelected] = useState<boolean>(false);

  return { dispatch, getState, isSelected, setSelected, size, sizes };
};

export const HomeSizes: FC = () => {
  const { dispatch, getState, isSelected, setSelected, size, sizes } = useSizes();

  const onChange = useCallback(
    (id: string) => {
      setSelected(false);
      setGarment(dispatch, getState, id);
    },
    [dispatch, getState, setSelected],
  );

  const onClick = useCallback(() => {
    setSelected(!isSelected);
  }, [isSelected, setSelected]);

  return (
    <S.Layout>
      {isSelected && <SizesPopup items={sizes} selection={size} onChange={onChange} />}
      <MenuButton
        isSelected={isSelected}
        text={size ? `Size: ${size}` : 'Size'}
        onClick={onClick}
      />
    </S.Layout>
  );
};
